/* eslint-disable react/prop-types */
import React, { useMemo, useState } from "react"
import * as t from "prop-types"
import { Button, Grid } from "@material-ui/core"
import { formatDate } from "utils/form-data"
import ItemDisplay from "components/common/ItemDisplay"
import MUIDataTable from "mui-datatables"
import InvoiceItemPrint from "components/invoice/InvoiceItemPrint"
import { useReactToPrint } from "react-to-print"
import { uniqBy } from "lodash"
import useShowSnackbar from "utils/snackbar-hooks"
import ApiInvoice from "services/api-invoice"
import { sendAndHandleInvalidToken } from "utils/invalid-token"
import { numberFormat } from "utils/thousand-separator"
import {
  ROUTE_DELIVERY_NOTE,
  ROUTE_SALES_QUOT_ROOT,
  ROUTE_WORK_ORDER,
} from "constants/routeConstants"
import ApiSalesQuotation from "services/api-sales-quotation"
import ApiWorkOrder from "services/api-work-order"
import {
  currencyOperation,
  generateProductDescriptions,
  generateTotalAmountPrint,
} from "./utils"
import InvoiceMergeForm from "./InvoiceMergeForm"
import AdditionalCostDisplay from "./AdditionalCostDisplay"
import InvoicePrintSettingDialog from "./InvoicePrintSettingDialog"
import EnableReviseToggle from "./EnableReviseToggle"
import { useStyles } from "./const"
import InvoiceItemDisplayHeader from "./InvoiceItemDisplayHeader"

function InvoiceItemDisplay({
  showEdit,
  onConfirmEdit,
  data,
  companyInformation,
  onConfirmChangeStatus,
  id,
  setFlagRefetch,
  canUpdatePaymentStatus,
  onUpdatePaymentStatus,
  canUpdateEnableReviseSQ,
  canCreateReceiptOfInvoice,
  onConfirmCreateReceiptOfInvoice,
  onEnableReviseSQChange = new Promise((res) => res(true)),
}) {
  const classes = useStyles()
  const [showSnackbar, showSnackbarLoading, hideSnackbar] = useShowSnackbar()
  const [modalOpen, setModalOpen] = useState(false)

  const [printSetting, setPrintSetting] = useState({})

  const [selections, setSelections] = useState([])

  const descriptions = React.useMemo(() => {
    return data.productDescriptions || []
  }, [data])

  const generalFields = [
    {
      attr: "invoiceNumber",
      label: "Invoice Number",
      display: (val) => {
        return val.replace(/-DELETED-\d+/, "")
      },
    },

    {
      attr: "date",
      label: "Invoice Date",
      display: (date) => formatDate(date),
    },
    {
      attr: "payment",
      label: "Payment",
      display: (payment) => payment ?? "-",
    },
    {
      attr: "paymentPercentage",
      label: "%",
      display: (paymentPercentage) => paymentPercentage ?? "-",
    },
    {
      attr: "authorizedSignature",
      label: "Signed By",
      display: (authorizedSignature) => authorizedSignature?.name ?? "-",
    },
    { attr: "remarks", label: "Remarks" },
    {
      attr: "notes",
      label: "Notes",
      display: (val) => <span style={{ whiteSpace: "pre-line" }}>{val}</span>,
    },
    { attr: "paymentTerms", label: "Payment Terms" },
    {
      attr: "paidDate",
      label: "Paid Date",
      display: (val) => (val ? formatDate(val) : "-"),
    },
  ]

  const customerFields = [
    {
      attr: "customer",
      label: "Customer",
      display: (customer) => customer?.name ?? "-",
    },
    { attr: "address", label: "Address" },
    { attr: "attn", label: "Attn" },
    {
      attr: "customer",
      label: "NPWP",
      display: (customer) => customer?.npwp ?? "-",
    },
    {
      attr: "currency",
      label: "Invoice Currency",
      display: (currency) => `${currency?.description} (${currency?.code})`,
    },
    {
      attr: "currencyRate",
      label: "Currency Rate",
    },
  ]

  const itemDisplayProps = {
    data,
    showEditButton: false,
    showDeleteButton: false,
  }

  const invNumber = data.invoiceNumber.replace(/-DELETED-\d+/, "")

  const title = `Invoice of “${invNumber}”`

  const tableNumberColumn = {
    name: "no",
    label: "No",
    options: {
      setCellHeaderProps: () => ({
        style: {
          width: "1px",
        },
      }),
      customBodyRenderLite: (index) => index + 1,
    },
  }

  const tableOptions = {
    elevation: 0,
    download: false,
    print: false,
    selectToolbarPlacement: "none",
    responsive: "standard",
    selectableRows: "multiple",
    isRowSelectable: (dataIndex) => {
      const item = data.productDescriptions[dataIndex]
      return !item?.invoiceDetailMergeId
    },
    onTableChange: (...args) => {
      const [action, tableState] = args
      if (action === "rowSelectionChange") {
        const indexes = tableState.selectedRows.data.map(
          (item) => item.dataIndex
        )
        setTimeout(() => {
          setSelections(indexes.map((idx) => descriptions[idx].idInvoiceDetail))
        }, 1000)
      }
    },
  }

  const standardColumn = [tableNumberColumn]

  const columnDebitNoteOptions = useMemo(() => {
    return [
      ...standardColumn,
      {
        name: "description",
        label: "Description",
        options: {
          customBodyRender: (description) => {
            return <span style={{ whiteSpace: "pre-line" }}>{description}</span>
          },
        },
      },
      { name: "quantity", label: "Quantity" },
      { name: "unit", label: "Unit" },
      {
        name: "unitPrice",
        label: "Unit Price",
        options: {
          customBodyRender: (unitPrice) => {
            return `${new Intl.NumberFormat().format(unitPrice)}`
          },
        },
      },
      {
        name: "totalCost",
        label: "Total Cost",
        options: {
          customBodyRenderLite: (index) => {
            const item = data.productDescriptions[index]

            const unitPrice = currencyOperation(
              item.unitPrice,
              data.rateIn,
              data.currencyRate
            )
            const percentage = item.paymentPercentage ?? data.paymentPercentage
            if (!percentage)
              return `${new Intl.NumberFormat().format(
                unitPrice * (item.quantity || 1)
              )}`
            return `${new Intl.NumberFormat().format(
              unitPrice * (0 + percentage / 100)
            )}`
          },
        },
      },
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.currencyRate, data.paymentPercentage, data.productDescriptions])

  const dnNumberUnique = useMemo(() => {
    return Array.from(
      new Set(
        data.productDescriptions.map(
          (item) => item.productDetail?.deliveryNote?.dnNumber
        )
      )
    )
  }, [data.productDescriptions])

  const poNumberUnique = useMemo(() => {
    return uniqBy(
      data.productDescriptions
        .map((item) => {
          const source =
            item.productDetail?.salesEstimate || item?.slsEstimate || {}

          return {
            ...source.slsPurchaseOrder,
            slsQuotNumber: source?.slsQuot?.slsQuotNumber,
          }
        })
        .filter((item) => item),
      "poNumber"
    )
  }, [data.productDescriptions])

  const poDateUnique = useMemo(() => {
    return uniqBy(
      data.productDescriptions
        .map(
          (item) =>
            item.productDetail?.salesEstimate?.slsPurchaseOrder ??
            item?.slsEstimate?.slsPurchaseOrder
        )
        .filter((item) => item),
      "poDate"
    )
  }, [data.productDescriptions])

  const columnInvoiceOptions = useMemo(
    () => {
      const col = [
        ...standardColumn,
        {
          name: "dnNumber",
          label: "DN Number",
          options: {
            customBodyRenderLite: (index) => {
              const item = data.productDescriptions[index]
              if (item?.productDetail?.deliveryNote) {
                return (
                  <Button
                    onClick={() => {
                      window.open(
                        `${ROUTE_DELIVERY_NOTE}/${item.productDetail.deliveryNoteId}`
                      )
                    }}
                  >
                    {item?.productDetail?.deliveryNote?.dnNumber}
                  </Button>
                )
              }
              return item?.productDetail?.deliveryNote?.dnNumber ?? "-"
            },
          },
        },
        {
          name: "poNumber",
          label: "PO Number",
          options: {
            customBodyRenderLite: (index) => {
              const item = data.productDescriptions[index]
              return (
                item?.productDetail?.salesEstimate?.slsPurchaseOrder
                  ?.poNumber ??
                item?.slsEstimate?.slsPurchaseOrder?.poNumber ??
                "-"
              )
            },
          },
        },
        {
          name: "refQuot",
          label: "Ref Quot",
          options: {
            customBodyRenderLite: (index) => {
              const item = data.productDescriptions[index]
              const slsQuotNumber =
                item?.productDetail?.salesEstimate?.slsQuot?.slsQuotNumber ??
                item?.slsEstimate?.slsQuot?.slsQuotNumber

              if (slsQuotNumber) {
                return (
                  <Button
                    onClick={async () => {
                      const res = (
                        await ApiSalesQuotation.get({
                          searchText: slsQuotNumber,
                          fromColumns: ["slsQuotNumber"],
                        })
                      ).data

                      window.open(
                        `${ROUTE_SALES_QUOT_ROOT}/${res[0].idSalesQuotation}`
                      )
                    }}
                  >
                    {slsQuotNumber}
                  </Button>
                )
              }
              return slsQuotNumber ?? "-"
            },
          },
        },
        {
          name: "description",
          label: "Description",
          options: {
            customBodyRender: (val) => (
              <span style={{ whiteSpace: "pre-line" }}>{val}</span>
            ),
          },
        },

        {
          name: "productDetail",
          label: "Mould Code",
          options: {
            customBodyRenderLite: (index) => {
              const item = data.productDescriptions[index]
              const { relatedWorkOrderEstimate } = item?.slsEstimate || {}
              const mouldCode =
                (relatedWorkOrderEstimate?.edges[0] || {}).node?.mouldCode ?? ""

              const code = item?.productDetail?.mouldCode ?? mouldCode

              if (code) {
                return (
                  <Button
                    onClick={async () => {
                      const res = (
                        await ApiWorkOrder.getList({
                          mouldCode: code,
                        })
                      ).data
                      window.open(`${ROUTE_WORK_ORDER}/${res[0].idWorkOrder}`)
                    }}
                  >
                    {code}
                  </Button>
                )
              }
              return ""
            },
          },
        },
        { name: "quantity", label: "Quantity" },
        { name: "unit", label: "Unit" },
        {
          name: "unitPrice",
          label: "Unit Price",
          options: {
            customBodyRender: (unitPrice) => {
              return numberFormat(
                currencyOperation(unitPrice, data.rateIn, data.currencyRate),
                0,
                ",",
                "."
              )
            },
          },
        },
        {
          name: "totalCost",
          label: "Total Cost",
          options: {
            customBodyRenderLite: (index) => {
              const item = data.productDescriptions[index]

              const unitPrice = currencyOperation(
                item.unitPrice,
                data.rateIn,
                data.currencyRate
              )

              let result = unitPrice

              const percentage =
                item.paymentPercentage ?? data.paymentPercentage

              if (percentage) {
                result = unitPrice * (0 + percentage / 100)
              }

              return numberFormat(result, 0, ",", ".")
            },
          },
        },
      ]

      if (data.invoiceType === "INVOICE EXPORT") {
        col.push({
          name: "remark",
          label: "Remark",
          options: {
            customBodyRender: (val) => (
              <span style={{ whiteSpace: "pre-line" }}>{val}</span>
            ),
          },
        })
      }

      return col
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data]
  )

  const subTotal = useMemo(() => {
    const { paymentPercentage, productDescriptions } = data
    const amount = productDescriptions.reduce((a, b) => {
      let percentage = b.paymentPercentage
      if (!percentage) {
        percentage = paymentPercentage ?? 100
      }

      return (
        a +
        Math.round(
          currencyOperation(+b.unitPrice, data.rateIn, data.currencyRate) *
            (0 + percentage / 100)
        )
      )
    }, 0)

    const additionalCost = productDescriptions.reduce((a, b) => {
      return (
        a + b.additionalCosts.reduce((c, d) => c + d.unitPrice * d.quantity, 0)
      )
    }, 0)

    return Math.round(amount + additionalCost)
  }, [data])

  console.log(subTotal)

  const totalCost = useMemo(() => {
    const baseCost = subTotal - data.discount

    if (data?.vat === 12) {
      return Math.round(
        baseCost + (((11 / data.vat) * baseCost * data.vat) / 100 ?? 1)
      )
    }

    return Math.floor(baseCost * (1 + (data?.vat / 100 ?? 1)))
  }, [data, subTotal])

  const componentRef = React.useRef()
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  })

  const isDebitNote = useMemo(() => data.invoiceType === "DEBIT NOTE", [
    data.invoiceType,
  ])

  const projectNameRows = useMemo(() => {
    const arr = []
    data.productDescriptions.forEach((pd) => {
      const relatedWorkOrder =
        pd.productDetail?.salesEstimate?.relatedWorkOrderEstimate?.edges ||
        pd.slsEstimate?.relatedWorkOrderEstimate?.edges ||
        []
      const currentProject = Array.from(
        new Set(
          (relatedWorkOrder || []).map(
            (edge) => edge?.node?.workOrder?.projectName
          )
        )
      ).join(", ")
      arr.push(!arr.includes(currentProject) ? currentProject : "")
    })
    return arr
  }, [data.productDescriptions])

  const productDescriptionList = useMemo(() => {
    if (!data.productDescriptions) return []
    return data.productDescriptions.map((product) => {
      const unitPrice =
        data.invoiceType === "DEBIT NOTE"
          ? product.unitPrice
          : product.unitPrice / product.quantity
      return {
        ...product,
        unitPrice: Math.ceil(unitPrice),
      }
    })
  }, [data.productDescriptions, data.invoiceType])

  const productDescriptions = useMemo(() => {
    if ((data?.mergedDetails || []).length) {
      return data.mergedDetails.map((product, i) => {
        const unitPrice = Math.ceil(
          product.details.reduce((a, b) => {
            return a + b.unitPrice
          }, 0)
        )
        const obj = generateProductDescriptions({
          data,
          arr: [
            {
              ...product,
              paymentPercentage: 100,
              unitPrice,
            },
          ],
        })
        obj[0].totalForCalculation = product.details.reduce((a, b) => {
          const amount = currencyOperation(
            b.unitPrice,
            data.rateIn,
            data.currencyRate
          )
          return (
            a +
            Math.round(
              amount * ((b.paymentPercentage ?? data.paymentPercentage) / 100)
            )
          )
        }, 0)
        obj[0].total = generateTotalAmountPrint({
          data,
          total: obj[0].totalForCalculation,
        })
        obj[0].no = i + 1
        return obj[0]
      })
    }
    if (printSetting.showBreakdown) {
      const arr = []
      productDescriptionList.forEach((pd, index) => {
        const { relatedWorkOrderEstimate, details } = pd?.slsEstimate || {}
        const { details: detailsFromDN } =
          pd?.productDetail?.salesEstimate || {}
        const mouldCode =
          (relatedWorkOrderEstimate?.edges[0] || {}).node?.mouldCode ?? "-"

        const choosenDetails = details || detailsFromDN || []

        if (choosenDetails.length) {
          choosenDetails.forEach((d, i) => {
            arr.push({
              ...d,
              mouldCode: pd?.productDetail?.mouldCode ?? mouldCode,
              projectName: i === 0 ? projectNameRows[index] : null,
              unit: d.uom?.name,
            })
          })
        } else {
          arr.push({
            ...pd,
            mouldCode: pd?.productDetail?.mouldCode ?? mouldCode,
            projectName: projectNameRows[index],
          })
        }
      })
      return generateProductDescriptions({ arr, data, isDebitNote })
    }

    return generateProductDescriptions({
      data,
      arr: data.productDescriptions.map((product, index) => {
        const { relatedWorkOrderEstimate } = product?.slsEstimate || {}
        const mouldCode =
          (relatedWorkOrderEstimate?.edges[0] || {}).node?.mouldCode ?? "-"

        return {
          ...product,
          mouldCode: product?.productDetail?.mouldCode ?? mouldCode,
          projectName: projectNameRows[index],
        }
      }),
      isDebitNote,
      mergeAdditionalCost: printSetting.mergeAdditionalCost,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data,
    isDebitNote,
    productDescriptionList,
    projectNameRows,
    printSetting.mergeAdditionalCost,
    printSetting.showBreakdown,
  ])

  const handleMergeInvoice = async (val) => {
    let fetchState = {}
    try {
      showSnackbarLoading("Loading...")

      if (selections.length) {
        fetchState = await sendAndHandleInvalidToken(() =>
          ApiInvoice.mergeInvoiceDetail({
            ...val,
            invoiceDetailId: selections.map((s) => parseInt(s, 10)),
            invoiceId: parseInt(id, 10),
          })
        )
      } else {
        fetchState = await sendAndHandleInvalidToken(() =>
          ApiInvoice.updateMergeInvoiceDetail({
            ...val,
            invoiceDetailId: selections.map((s) => parseInt(s, 10)),
            invoiceId: parseInt(id, 10),
          })
        )
      }
      if (fetchState.success) {
        showSnackbar("Invoice detail merged successfully!")
        setFlagRefetch()
      } else {
        hideSnackbar()
      }
    } catch (error) {
      showSnackbar(error?.errors[0]?.message || "Something went wrong")
    }
  }

  const handleDeleteMergeInvoice = async (invoiceDetailMergeId) => {
    let fetchState = {}
    try {
      showSnackbarLoading("Loading...")

      fetchState = await sendAndHandleInvalidToken(() =>
        ApiInvoice.deleteMergeInvoiceDetail(parseInt(invoiceDetailMergeId, 10))
      )
      if (fetchState.success) {
        showSnackbar("Delete susccess")
        setFlagRefetch()
      } else {
        hideSnackbar()
      }
    } catch (error) {
      showSnackbar("Something went wrong")
    }
  }

  const handlePrintSettingClose = (val) => {
    setPrintSetting(val)
    setModalOpen(false)
    setTimeout(() => {
      handlePrint()
    }, 1000)
  }

  return (
    <>
      <InvoiceItemPrint
        companyInformation={companyInformation}
        ref={componentRef}
        data={data}
        productDescription={productDescriptions}
        dnNumberUnique={dnNumberUnique}
        poDateUnique={poDateUnique}
        poNumberUnique={poNumberUnique}
        {...printSetting}
      />
      <InvoicePrintSettingDialog
        open={modalOpen}
        onClose={handlePrintSettingClose}
      />

      <InvoiceItemDisplayHeader
        classes={classes}
        showEdit={showEdit}
        data={data}
        title={title}
        onConfirmEdit={onConfirmEdit}
        onConfirmChangeStatus={onConfirmChangeStatus}
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        canCreateReceiptOfInvoice={canCreateReceiptOfInvoice}
        onConfirmCreateReceiptOfInvoice={onConfirmCreateReceiptOfInvoice}
      />

      {canUpdatePaymentStatus && data?.paymentStatus !== "paid" ? (
        <Button
          color="primary"
          variant="contained"
          onClick={onUpdatePaymentStatus}
        >
          Mark as Paid
        </Button>
      ) : null}
      <Grid container>
        <Grid item xs={12} sm={6}>
          <ItemDisplay
            {...itemDisplayProps}
            attrsAndLabels={generalFields}
            title="General Data"
          />

          {canUpdateEnableReviseSQ ? (
            <EnableReviseToggle
              defaultValue={data.enableRevise}
              onEnableReviseSQChange={onEnableReviseSQChange}
            />
          ) : null}
        </Grid>

        <Grid item xs={12} sm={6}>
          <ItemDisplay
            {...itemDisplayProps}
            attrsAndLabels={customerFields}
            title="Customer Data"
          />
        </Grid>
      </Grid>

      <div className={classes.tableWrapper}>
        <MUIDataTable
          title={
            <div className={classes.tableTitle}>
              <strong>Description</strong>
            </div>
          }
          data={productDescriptionList}
          columns={
            data.invoiceType !== "DEBIT NOTE"
              ? columnInvoiceOptions
              : columnDebitNoteOptions
          }
          options={tableOptions}
        />
      </div>

      <div className={classes.tableWrapper}>
        <AdditionalCostDisplay productDesctiptions={data.productDescriptions} />
      </div>

      <table className={classes.sectionTotalTable}>
        <tbody>
          <tr>
            <th>Subtotal</th>
            <th>{new Intl.NumberFormat().format(Math.ceil(subTotal))}</th>
          </tr>
          <tr>
            <th>Discount</th>
            <th>{new Intl.NumberFormat().format(data.discount ?? 0)}</th>
          </tr>
          {data?.vat === 12 ? (
            <tr>
              <th>DPP</th>
              <th>
                {new Intl.NumberFormat().format(
                  Math.floor((11 / data.vat) * (subTotal - data.discount))
                )}
              </th>
            </tr>
          ) : null}
          <tr>
            <th>VAT ({data.vat ?? 0}%)</th>
            <th>
              {data?.vat === 12
                ? new Intl.NumberFormat().format(
                    Math.round(
                      (11 / data.vat) *
                        (subTotal - data.discount) *
                        (0 + data?.vat / 100 ?? 0)
                    )
                  )
                : new Intl.NumberFormat().format(
                    Math.floor(
                      (subTotal - data.discount) * (0 + data?.vat / 100 ?? 0)
                    )
                  )}
            </th>
          </tr>
          <tr>
            <th>Total</th>
            <th>{new Intl.NumberFormat().format(totalCost)}</th>
          </tr>
        </tbody>
      </table>
      {showEdit && (
        <InvoiceMergeForm
          disabled={!selections.length}
          onSubmit={handleMergeInvoice}
          defaultValue={data.mergedDetails}
          handleDeleteMergeInvoice={handleDeleteMergeInvoice}
        />
      )}
    </>
  )
}

InvoiceItemDisplay.propTypes = {
  showEdit: t.bool.isRequired,
  onConfirmEdit: t.func.isRequired,
  data: t.shape({
    invoiceNumber: t.string.isRequired,
    invoiceType: t.string.isRequired,
    paymentPercentage: t.number,
    date: t.string.isRequired,
    customer: t.shape({
      name: t.string.isRequired,
    }),
    deliveryNote: t.shape({
      dnNumber: t.string,
      poNumber: t.string,
      poDate: t.string,
      salesQuotationNumber: t.shape({
        slsQuotNumber: t.string,
      }),
    }),
    currency: t.shape({
      code: t.string,
      description: t.string,
    }),
    currencyRate: t.number,
    productDescriptions: t.arrayOf(
      t.shape({
        description: t.string,
        unitPrice: t.number,
        productDetail: t.shape({
          mouldCode: t.string,
        }),
      })
    ),
    discount: t.number,
    vat: t.number,
  }).isRequired,
  companyInformation: t.shape({
    companyName: t.string,
    phone: t.string,
    email: t.string,
    address: t.string,
  }).isRequired,
}

export default InvoiceItemDisplay
